import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  TextField,
  InputAdornment,
  Badge,
} from "@mui/material";
import { Link } from "react-router-dom";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import SearchIcon from "@mui/icons-material/Search";
import logo from "../img/logo.jpg";
import bannerImg from "../img/banner.webp";
import { useSelector } from "react-redux"; // Import useSelector to access Redux store
import { setSearchQuery } from "../store/searchSlice";
import { useDispatch } from "react-redux";
import { useMediaQuery, useTheme } from "@mui/material";

const Banner: React.FC = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [bannerHeight, setBannerHeight] = useState(100);
  const [isScrolled, setIsScrolled] = useState(false);
  // Use Redux selector to get the total number of items in the cart
  const cartItemsCount = useSelector((state: any) => state.cart.totalItems); // Adjust state path according to your store structure
  const dispatch = useDispatch();
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchQuery(event.target.value)); // Update search query in Redux store
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100); // Compact banner when user scrolls 50px
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <AppBar
      position="sticky"
      sx={{
        width: "100vw",
        transition: "all 0.5s ease",
        overflow: "hidden", // Prevent scrollbars in case of slight overflow
        backgroundImage: "linear-gradient(90deg, #E7BAD1 0%, #7FBEEB 100%)",
        zIndex: 10,
        top: 0,
        backgroundSize: "cover",
        backgroundPosition: "center",
        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.3)", // Adding shadow to the AppBar
        height: { xs: "90px", sm: "110px", md: "160px" }, // Fixed height
        transform: { xs: "translateY(0)", sm: "translateY(0)", md: isScrolled ? "translateY(-80px)" : "translateY(0)" }, // Visual effect
        margin: 0, // Remove any potential margin
        padding: 0, // Remove padding
      }}
    >
      {/* Background Image Layer */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          height: "100%",
          backgroundImage: `url(${bannerImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transition: "height 0.5s ease",
          opacity: 0.2,
          zIndex: 0,
        }}
      />

      {/* Welcome Text for Tablets and Mobile */}
      {isMobileOrTablet && (
        <Box
          sx={{
            position: "absolute",
            top: "3px",
            left: 0,
            right: 0,
            textAlign: "center",
            flexGrow: 1,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: "white",
              fontWeight: "bold",
              fontSize: { xs: "18px", sm: "28px" },
              padding: "20px",
            }}
          >
            Welcome to Cozy Bud
          </Typography>
        </Box>
      )}

      {/* Toolbar Section */}
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "100%", // Ensure it matches the parent AppBar height
          paddingX: { xs: "8px", sm: "16px", md: "24px" },
        }}
      >
        {!isMobileOrTablet && (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <img
              src={logo}
              alt="Cozy Bud Logo"
              style={{
                height: isScrolled ? "70px" : "140px", // Shrinks dynamically
                transition: "height 0.8s ease",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)", // Shadow for logo
                borderRadius: "6px", // Rounded corners for a smoother look
                transform: isScrolled ? "translateY(39px)" : "translateY(0)", // Visual effect
              }}
            />
          </Box>
        )}
        {isMobileOrTablet && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={logo}
              alt="Cozy Bud Logo"
              style={{
                height: "70px",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)", // Shadow for logo
                borderRadius: "6px", // Rounded corners for a smoother look
              }}
            />
          </Box>
        )}
        {/* Center Content */}
        {!isScrolled && (
          <Box
            sx={{
              textAlign: "center",
              flexGrow: 1,
              display: { xs: "none", md: "block" },
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: "white",
                fontWeight: "bold",
                fontSize: { xs: "16px", sm: "20px", md: "32px" },
              }}
            >
              Welcome to Cozy Bud
            </Typography>
            <Typography
              variant="h1"
              sx={{
                color: "white",
                fontSize: { xs: "12px", sm: "14px", md: "16px" },
              }}
            >
              An online destination for adorable and high-quality baby wear
            </Typography>
            <TextField
              variant="outlined"
              placeholder="Search..."
              size="small"
              sx={{
                marginTop: "10px",
                width: { xs: "150px", sm: "200px", md: "400px" },
                borderRadius: "20px",
                backgroundColor: "#DBD8F0",
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "transparent", // Remove outline on hover
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "transparent", // Remove outline on focus
                    boxShadow: "0 0 0 2px rgba(127, 190, 235, 0.4)", // Optional: Add a subtle shadow effect on focus
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent", // Remove initial border
                },
              }}
              onChange={handleSearchChange} // Update search query
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {/* Welcome Text, Logo, Cart Icon, and Home Icon on Scroll */}
        {!isMobileOrTablet && isScrolled && (
          <Box
            sx={{
              textAlign: "center",
              flexGrow: 1,
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              paddingTop: "80px",
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: "white",
                fontWeight: "bold",
                opacity: 1,
                transition: "opacity 0.5s ease",
              }}
            >
              Welcome to Cozy Bud
            </Typography>
          </Box>
        )}

        {/* Search Bar for Mobile/Tablet */}
        {isMobileOrTablet && (
          <Box
            sx={{
              flexGrow: 1,
              pt: { xs: "40px", sm: "50px" },
              marginLeft: { xs: "10px", sm: "60px" },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <TextField
              variant="outlined"
              placeholder="Search..."
              size="small"
              sx={{
                width: { xs: "150px", sm: "300px" }, // Compact width for mobile and tablet
                borderRadius: "20px",
                backgroundColor: "#DBD8F0",
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "transparent", // Remove outline on hover
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "transparent", // Remove outline on focus
                    boxShadow: "0 0 0 2px rgba(127, 190, 235, 0.4)", // Optional: Add a subtle shadow effect on focus
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent", // Remove initial border
                },
                input: {
                  padding: "6px 8px", // Compact padding inside the text field
                  fontSize: { xs: "11px", sm: "13px" }, // Smaller font size
                },
              }}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: { xs: "15px", sm: "18px" } }} />{" "}
                    {/* Scaled icon */}
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {/* Icons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            paddingTop: { xs: "40px", md: "80px" },
          }}
        >
          <Button color="success" component={Link} to="/" aria-label="Home">
            <HomeOutlinedIcon
              sx={{
                fontSize: {
                  xs: "24px",
                  sm: "30px",
                  md: isScrolled ? "30px" : "40px",
                },
              }}
            />
          </Button>
          <Button
            color="success"
            component={Link}
            to="/shoppingcart"
            aria-label="Shopping Cart"
          >
            <Badge
              badgeContent={cartItemsCount}
              color="error"
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <ShoppingCartOutlinedIcon
                sx={{
                  fontSize: {
                    xs: "24px",
                    sm: "30px",
                    md: isScrolled ? "30px" : "40px",
                  },
                }}
              />
            </Badge>
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Banner;
